/**
 * Hook for fetching and managing the conversation list.
 *
 * Calls GET /api/copilot/conversations and exposes:
 * - conversations: the current list
 * - loading / error: request state
 * - refetch: manually reload
 * - hasMore: whether pagination has more items
 * - loadMore: fetch next page
 */

import { useState, useCallback, useRef } from "react";
import { copilotApi } from "./useApi";
import type {
  ConversationSummary,
  ConversationListResponse,
} from "../types";

const PAGE_SIZE = 20;

export interface UseConversationsReturn {
  conversations: ConversationSummary[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  /** Reload from offset 0. */
  refetch: () => Promise<void>;
  /** Load the next page and append. */
  loadMore: () => Promise<void>;
  /** Optimistically remove a conversation from the local list. */
  removeFromList: (id: string) => void;
  /** Optimistically update a conversation title in the local list. */
  updateTitleInList: (id: string, title: string) => void;
}

export function useConversations(): UseConversationsReturn {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const res = await copilotApi.get<ConversationListResponse>(
        "/copilot/conversations",
        { params: { limit: PAGE_SIZE, offset } },
      );
      const items = res.data.conversations;
      setConversations((prev) => (append ? [...prev, ...items] : items));
      setHasMore(items.length === PAGE_SIZE);
      offsetRef.current = offset + items.length;
    } catch {
      setError("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    offsetRef.current = 0;
    await fetchPage(0, false);
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    await fetchPage(offsetRef.current, true);
  }, [loading, hasMore, fetchPage]);

  const removeFromList = useCallback((id: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const updateTitleInList = useCallback((id: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title } : c)),
    );
  }, []);

  return {
    conversations,
    loading,
    error,
    hasMore,
    refetch,
    loadMore,
    removeFromList,
    updateTitleInList,
  };
}
